import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  Link,
  useBlocker,
  useNavigate,
} from 'react-router-dom'

import { ApiError } from '@/lib/api/errors'
import { friendlyErrorKey } from '@/lib/api/friendly-errors'
import { useSessionStore } from '@/lib/auth/session-store'
import { categoryCoverUrl } from '@/lib/category-covers'
import { localizedCategoryName } from '@/lib/category-labels'
import { kuniriIconWeight, KuniriIcons } from '@/lib/icons'
import { resolveMediaUrl } from '@/lib/media'

import './activities.css'
import { activityComposerApi } from './api'
import { ConfirmDialog } from './components/confirm-dialog'
import { CoverField } from './components/cover-field'
import { DateTimeField } from './components/date-time-field'
import { FormSection } from './components/form-section'
import {
  SelectField,
  TextAreaField,
  TextField,
} from './components/form-fields'
import { LocationFields } from './components/location-fields'
import { Notice } from './components/notice'
import { ProfileGate, type ProfileCheck } from './components/profile-gate'
import {
  clearDraft,
  loadDraft,
  saveDraft,
  type ActivityDraft,
} from './draft'
import { useLocationSelection } from './hooks/use-location-selection'
import { localWeekday, weekdayKey } from './recurrence'
import {
  activityFormSchema,
  normalizePrice,
} from './schemas'
import type {
  ActivityDetail,
  Category,
  CreateActivityRequest,
  ParticipationMode,
  UpdateActivityRequest,
} from './types'
import {
  activityToFormValues,
  addHoursToLocalInput,
  EMPTY_FORM_VALUES,
  localInputToIso,
  type ActivityFormValues,
} from './values'
import { zodResolver } from './zod-resolver'

const MAX_COVER_BYTES = 20 * 1024 * 1024
const ALLOWED_COVER_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
])

function bytesMatch(bytes: Uint8Array, offset: number, signature: number[]): boolean {
  if (bytes.length < offset + signature.length) return false
  return signature.every((value, index) => bytes[offset + index] === value)
}

function looksLikeAllowedImage(bytes: Uint8Array): boolean {
  const jpeg = bytesMatch(bytes, 0, [0xff, 0xd8, 0xff])
  const png = bytesMatch(bytes, 0, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])
  const webp =
    bytesMatch(bytes, 0, [0x52, 0x49, 0x46, 0x46]) &&
    bytesMatch(bytes, 8, [0x57, 0x45, 0x42, 0x50])
  return jpeg || png || webp
}

interface ActivityFormProps {
  mode: 'create' | 'edit'
  categories: Category[]
  activity?: ActivityDetail
  profile?: ProfileCheck
  onProfileRetry?: () => void
}

function submitErrorKey(error: unknown, mode: 'create' | 'edit'): string {
  if (error instanceof ApiError) {
    if (error.status === 400) {
      return mode === 'create'
        ? 'activityForm.errors.createValidation'
        : 'activityForm.errors.updateValidation'
    }
    if (error.status === 403 && mode === 'create') {
      return 'activityForm.errors.createNotAllowed'
    }
    return friendlyErrorKey(error)
  }
  return mode === 'create'
    ? 'activityForm.errors.create'
    : 'activityForm.errors.update'
}

/**
 * Shared create/edit form.
 *
 * Create: POST /activities then the optional cover; a cover failure keeps the
 * saved activity and exposes an explicit retry. The local draft is tab-scoped
 * and is not a backend status change (the frozen contract always publishes).
 *
 * Edit: PATCH /activities/{id}; when the city/location name are unchanged the
 * existing coordinates are reused, so the user is not forced to re-pick a
 * suggestion (`f472f9a`). Cancel uses DELETE and is only reachable before start.
 */
export function ActivityForm({
  mode,
  categories,
  activity,
  profile,
  onProfileRetry,
}: ActivityFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const userId = useSessionStore((state) => state.user?.id ?? null)
  const [restoredDraft] = useState<ActivityDraft | null>(() =>
    mode === 'create' ? loadDraft(userId) : null,
  )

  const defaultValues = useMemo<ActivityFormValues>(() => {
    if (mode === 'edit' && activity) {
      const matching = categories.find(
        (category) => category.slug === activity.categorySlug,
      )
      return activityToFormValues(activity, matching?.id ?? '')
    }
    return restoredDraft?.values ?? { ...EMPTY_FORM_VALUES }
  }, [activity, categories, mode, restoredDraft])

  const form = useForm<ActivityFormValues>({
    resolver: zodResolver(activityFormSchema),
    defaultValues,
    mode: 'onSubmit',
  })
  const {
    control,
    formState: { errors, isDirty, isSubmitting },
    getValues,
    handleSubmit,
    register,
    reset,
    setError,
    setFocus,
    setValue,
    trigger,
  } = form

  const location = useLocationSelection({
    form,
    mode,
    activity,
    initialLocation: restoredDraft?.location,
  })

  const selectedCategoryId = useWatch({ control, name: 'categoryId' })
  const hasExpenses = useWatch({ control, name: 'hasExpenses' })
  const participationMode = useWatch({ control, name: 'participationMode' })
  const visibility = useWatch({ control, name: 'visibility' })
  const startAtValue = useWatch({ control, name: 'startAt' })
  const selectedCategory = categories.find(
    (category) => category.id === selectedCategoryId,
  )
  const recurringEdit =
    mode === 'edit' && activity?.isRecurring === true
  const recurrenceLocked = recurringEdit
  const recurrenceWeekday = weekdayKey(localWeekday(startAtValue))
  const allowPrivateVisibility =
    mode === 'edit' && activity?.visibility === 'private'

  const [draftNotice, setDraftNotice] = useState<string | null>(
    restoredDraft ? 'activityForm.draft.restored' : null,
  )
  const [draftAvailable, setDraftAvailable] = useState(Boolean(restoredDraft))
  const [saveError, setSaveError] = useState<string | null>(null)
  const [createdId, setCreatedId] = useState<string | null>(null)
  const [coverFile, setCoverFile] = useState<File | null>(null)
  const [coverPreviewUrl, setCoverPreviewUrl] = useState<string | null>(null)
  const [coverError, setCoverError] = useState<string | null>(null)
  const [coverRetryError, setCoverRetryError] = useState<string | null>(null)
  const [coverFailure, setCoverFailure] = useState<{ id: string } | null>(null)
  const [coverBusy, setCoverBusy] = useState(false)
  const [cancelOpen, setCancelOpen] = useState(false)
  const [cancelBusy, setCancelBusy] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)
  const [allowLeave, setAllowLeave] = useState(false)
  const [pendingDestination, setPendingDestination] = useState<{
    to: string
    replace: boolean
  } | null>(null)

  const previewUrlRef = useRef<string | null>(null)
  useEffect(
    () => () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL?.(previewUrlRef.current)
      }
    },
    [],
  )

  const profileBlocked =
    mode === 'create' &&
    profile !== undefined &&
    (profile.status === 'loading' ||
      (profile.status === 'ready' && !profile.profile.avatarUrl))

  const dirty = isDirty || coverFile !== null
  const blocker = useBlocker(dirty && !allowLeave)

  useEffect(() => {
    if (!dirty || allowLeave) return
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [allowLeave, dirty])

  useEffect(() => {
    if (!allowLeave || !pendingDestination) return
    navigate(pendingDestination.to, { replace: pendingDestination.replace })
  }, [allowLeave, navigate, pendingDestination])

  function fieldError(message?: string): string | undefined {
    return message ? t(message) : undefined
  }

  function replacePreview(file: File | null): void {
    if (previewUrlRef.current) {
      URL.revokeObjectURL?.(previewUrlRef.current)
      previewUrlRef.current = null
    }
    if (!file || typeof URL.createObjectURL !== 'function') {
      setCoverPreviewUrl(null)
      return
    }
    const url = URL.createObjectURL(file)
    previewUrlRef.current = url
    setCoverPreviewUrl(url)
  }

  async function selectCover(file: File | null): Promise<void> {
    setCoverError(null)
    setCoverRetryError(null)
    setCoverFailure(null)
    if (!file) {
      setCoverFile(null)
      replacePreview(null)
      return
    }
    if (!ALLOWED_COVER_TYPES.has(file.type)) {
      setCoverFile(null)
      replacePreview(null)
      setCoverError('activityForm.cover.errorType')
      return
    }
    if (file.size === 0 || file.size > MAX_COVER_BYTES) {
      setCoverFile(null)
      replacePreview(null)
      setCoverError('activityForm.cover.errorSize')
      return
    }
    try {
      const buffer = await file.slice(0, 16).arrayBuffer()
      if (!looksLikeAllowedImage(new Uint8Array(buffer))) {
        setCoverFile(null)
        replacePreview(null)
        setCoverError('activityForm.cover.errorInvalid')
        return
      }
    } catch {
      setCoverFile(null)
      replacePreview(null)
      setCoverError('activityForm.cover.errorInvalid')
      return
    }
    setCoverFile(file)
    replacePreview(file)
  }

  function handleStartChange(value: string): void {
    if (!value) return
    const end = getValues('endAt')
    if (!end) {
      const next = addHoursToLocalInput(value, 1)
      if (next) {
        setValue('endAt', next, { shouldDirty: true, shouldValidate: false })
      }
      void trigger('endAt')
      return
    }
    const startTime = new Date(value).getTime()
    const endTime = new Date(end).getTime()
    if (
      Number.isNaN(startTime) ||
      Number.isNaN(endTime) ||
      endTime <= startTime ||
      endTime - startTime > 24 * 60 * 60 * 1000
    ) {
      const next = addHoursToLocalInput(value, 1)
      if (next) {
        setValue('endAt', next, { shouldDirty: true, shouldValidate: false })
      }
    }
    void trigger('endAt')
  }

  function buildPayload(
    values: ActivityFormValues,
  ): CreateActivityRequest | UpdateActivityRequest {
    const places = location.toPayload()
    const start = localInputToIso(values.startAt)
    const end = localInputToIso(values.endAt)
    if (!start || !end) {
      throw new Error('Activity dates could not be serialized')
    }
    return {
      title: values.title,
      description:
        mode === 'create' ? values.description.trim() : values.description,
      categoryId: values.categoryId,
      startAt: start,
      endAt: end,
      city: places.city,
      ...(places.locationName !== undefined
        ? { locationName: places.locationName }
        : {}),
      ...(places.latitude !== undefined
        ? { latitude: places.latitude, longitude: places.longitude }
        : {}),
      meetingPoint: values.meetingPoint.trim(),
      maxParticipants: Number(values.maxParticipants),
      participationMode: values.participationMode,
      visibility: values.visibility,
      priceType: values.hasExpenses ? 'external_estimate' : 'free',
      ...(mode === 'create' || values.repeatWeekly
        ? { repeatWeekly: values.repeatWeekly }
        : {}),
      ...(values.hasExpenses
        ? {
            estimatedPrice: normalizePrice(values.estimatedPrice),
            priceDescription: values.priceDescription.trim(),
          }
        : {}),
    }
  }

  function finish(id: string): void {
    clearDraft(userId)
    setAllowLeave(true)
    setPendingDestination({
      to: mode === 'create' ? '/home' : `/activity/${id}`,
      replace: true,
    })
  }

  async function submit(values: ActivityFormValues): Promise<void> {
    setSaveError(null)
    setCoverRetryError(null)
    if (coverFailure) return
    if (profileBlocked) {
      setSaveError('activityForm.profile.blocked')
      return
    }

    const locationError = location.validate()
    if (locationError) {
      setError(locationError.field, { message: locationError.message })
      setFocus(locationError.field)
      return
    }

    if (
      mode === 'edit' &&
      activity &&
      Number(values.maxParticipants) < activity.currentParticipantsCount
    ) {
      setError('maxParticipants', {
        message: 'activityForm.validation.maxParticipantsBelowCurrent',
      })
      setFocus('maxParticipants')
      return
    }

    let payload: CreateActivityRequest | UpdateActivityRequest
    try {
      payload = buildPayload(values)
    } catch {
      setSaveError(
        mode === 'create'
          ? 'activityForm.errors.create'
          : 'activityForm.errors.update',
      )
      return
    }

    let id: string
    try {
      if (mode === 'create') {
        if (createdId) {
          id = createdId
        } else {
          const response = await activityComposerApi.createActivity(
            payload as CreateActivityRequest,
          )
          id = response.id
          setCreatedId(id)
        }
      } else {
        if (!activity) {
          setSaveError('activityForm.errors.update')
          return
        }
        id = activity.id
        await activityComposerApi.updateActivity(
          id,
          payload as UpdateActivityRequest,
        )
      }
    } catch (error) {
      setSaveError(submitErrorKey(error, mode))
      return
    }

    if (coverFile) {
      try {
        await activityComposerApi.uploadCover(id, coverFile)
      } catch {
        setCoverFailure({ id })
        return
      }
    }

    finish(id)
  }

  async function retryCoverUpload(): Promise<void> {
    if (!coverFailure || !coverFile || coverBusy) return
    setCoverBusy(true)
    setCoverRetryError(null)
    try {
      await activityComposerApi.uploadCover(coverFailure.id, coverFile)
      finish(coverFailure.id)
    } catch {
      setCoverRetryError('activityForm.cover.errorUpload')
    } finally {
      setCoverBusy(false)
    }
  }

  function continueWithoutCover(): void {
    if (!coverFailure) return
    finish(coverFailure.id)
  }

  function storeDraft(): void {
    const saved = saveDraft(userId, {
      values: getValues(),
      location: {
        selectedCity: location.selectedCity,
        selectedPlace: location.selectedPlace,
        manualCity: location.manualCity,
        manualPlace: location.manualPlace,
      },
    })
    setDraftNotice(
      saved ? 'activityForm.draft.saved' : 'activityForm.draft.saveError',
    )
    if (saved) reset(getValues(), { keepValues: true })
    setDraftAvailable(saved)
  }

  function discardDraft(): void {
    clearDraft(userId)
    setDraftAvailable(false)
    setDraftNotice('activityForm.draft.discarded')
  }

  async function cancelActivity(): Promise<void> {
    if (!activity || cancelBusy) return
    setCancelBusy(true)
    setCancelError(null)
    try {
      await activityComposerApi.cancelActivity(activity.id)
      setCancelOpen(false)
      finish(activity.id)
    } catch (error) {
      setCancelOpen(false)
      setCancelError(friendlyErrorKey(error))
    } finally {
      setCancelBusy(false)
    }
  }

  const coverCategory = selectedCategory
    ? {
        url: categoryCoverUrl(selectedCategory.slug, selectedCategory.name),
        color: selectedCategory.color,
      }
    : { url: categoryCoverUrl('altro'), color: 'var(--mint)' }

  return (
    <>
      <form
        noValidate
        className="activity-form"
        onSubmit={handleSubmit(
          (values) => void submit(values),
          (invalid) => {
            const order: (keyof ActivityFormValues)[] = [
              'title',
              'categoryId',
              'startAt',
              'endAt',
              'city',
              'locationName',
              'maxParticipants',
              'estimatedPrice',
            ]
            const first = order.find((field) => invalid[field])
            if (first) setFocus(first)
          },
        )}
      >
        <FormSection
          title={t('activityForm.sections.basic')}
          icon={KuniriIcons.notePencil}
        >
          <div className="space-y-3">
            <TextField
              label={t('activityForm.fields.title')}
              placeholder={t('activityForm.fields.titlePlaceholder')}
              autoComplete="off"
              maxLength={120}
              required
              error={fieldError(errors.title?.message)}
              {...register('title')}
            />
            <TextAreaField
              label={t('activityForm.fields.description')}
              placeholder={t('activityForm.fields.descriptionPlaceholder')}
              rows={4}
              maxLength={3000}
              error={fieldError(errors.description?.message)}
              {...register('description')}
            />
            <SelectField
              label={t('activityForm.fields.category')}
              error={fieldError(errors.categoryId?.message)}
              required
              {...register('categoryId')}
            >
              <option value="">{t('activityForm.fields.categoryPlaceholder')}</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {localizedCategoryName(t, category.slug, category.name)}
                </option>
              ))}
            </SelectField>
          </div>
        </FormSection>

        {mode === 'create' && profile ? (
          <ProfileGate check={profile} onRetry={onProfileRetry ?? (() => {})} />
        ) : null}

        <FormSection
          title={t('activityForm.sections.when')}
          icon={KuniriIcons.clock}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Controller
              name="startAt"
              control={control}
              render={({ field, fieldState }) => (
                <DateTimeField
                  label={t('activityForm.fields.start')}
                  kind="start"
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value)
                    handleStartChange(value)
                  }}
                  onBlur={field.onBlur}
                  error={fieldError(fieldState.error?.message)}
                />
              )}
            />
            <Controller
              name="endAt"
              control={control}
              render={({ field, fieldState }) => (
                <DateTimeField
                  label={t('activityForm.fields.end')}
                  kind="end"
                  value={field.value}
                  min={startAtValue ? startAtValue.slice(0, 10) : undefined}
                  onChange={(value) => {
                    field.onChange(value)
                    if (value) void trigger('endAt')
                  }}
                  onBlur={field.onBlur}
                  error={fieldError(fieldState.error?.message)}
                />
              )}
            />
          </div>
          <label className="mt-3 flex items-start gap-3">
            <input
              type="checkbox"
              className="mt-1 size-5 shrink-0 accent-teal"
              disabled={recurrenceLocked}
              {...register('repeatWeekly')}
            />
            <span>
              <span className="block font-body text-base font-bold text-deep">
                {t('activityForm.recurrence.title')}
              </span>
              <span className="mt-1 block text-sm leading-relaxed text-muted">
                {recurrenceLocked
                  ? t('activityForm.recurrence.subtitleLocked')
                  : recurrenceWeekday
                    ? t('activityForm.recurrence.subtitleWeekday', {
                        weekday: t(recurrenceWeekday),
                      })
                    : t('activityForm.recurrence.subtitleNoStart')}
              </span>
            </span>
          </label>
        </FormSection>

        <FormSection
          title={t('activityForm.sections.location')}
          icon={KuniriIcons.mapPin}
        >
          <LocationFields
            location={location}
            cityError={fieldError(errors.city?.message)}
            placeError={fieldError(errors.locationName?.message)}
            disabled={isSubmitting}
          />
          <div className="mt-4">
            <TextField
              label={t('activityForm.fields.meetingPoint')}
              placeholder={t('activityForm.fields.meetingPointPlaceholder')}
              maxLength={300}
              error={fieldError(errors.meetingPoint?.message)}
              {...register('meetingPoint')}
            />
          </div>
        </FormSection>

        <FormSection
          title={t('activityForm.sections.participants')}
          icon={KuniriIcons.users}
        >
          <div className="space-y-3">
            <TextField
              label={t('activityForm.fields.maxParticipants')}
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              required
              error={fieldError(errors.maxParticipants?.message)}
              {...register('maxParticipants')}
            />
            <SelectField
              label={t('activityForm.fields.participation')}
              error={fieldError(errors.participationMode?.message)}
              {...register('participationMode')}
            >
              {(
                [
                  ['open', 'activityForm.participation.open'],
                  ['approval', 'activityForm.participation.approval'],
                  ['invite', 'activityForm.participation.invite'],
                ] as [ParticipationMode, string][]
              ).map(([value, key]) => (
                <option key={value} value={value}>
                  {t(key)}
                </option>
              ))}
            </SelectField>
            {participationMode === 'invite' ? (
              <p className="text-sm leading-relaxed text-muted">
                {t('activityForm.participation.inviteHint')}
              </p>
            ) : null}
            <SelectField
              label={t('activityForm.visibility.label')}
              {...register('visibility')}
            >
              <option value="public">
                {t('activityForm.visibility.options.public')}
              </option>
              <option value="friends">
                {t('activityForm.visibility.options.friends')}
              </option>
              {allowPrivateVisibility ? (
                <option value="private">
                  {t('activityForm.visibility.options.private')}
                </option>
              ) : null}
            </SelectField>
            <p className="text-sm leading-relaxed text-muted">
              {t(`activityForm.visibility.hints.${visibility}`)}
            </p>
          </div>
        </FormSection>

        <FormSection
          title={t('activityForm.sections.costs')}
          icon={KuniriIcons.money}
        >
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              role="switch"
              className="mt-1 size-5 shrink-0 accent-teal"
              {...register('hasExpenses')}
            />
            <span>
              <span className="block font-body text-base font-bold text-deep">
                {t('activityForm.costs.toggle')}
              </span>
              <span className="mt-1 block text-sm leading-relaxed text-muted">
                {t('activityForm.costs.toggleHint')}
              </span>
            </span>
          </label>
          {hasExpenses ? (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <TextField
                label={t('activityForm.fields.estimatedPrice')}
                inputMode="decimal"
                placeholder="0"
                trailing={<span className="pr-3 text-muted">€</span>}
                error={fieldError(errors.estimatedPrice?.message)}
                {...register('estimatedPrice')}
              />
              <TextField
                label={t('activityForm.fields.priceDescription')}
                maxLength={500}
                error={fieldError(errors.priceDescription?.message)}
                {...register('priceDescription')}
              />
            </div>
          ) : null}
        </FormSection>

        <FormSection
          title={t('activityForm.sections.cover')}
          icon={KuniriIcons.imageSquare}
        >
          <CoverField
            previewUrl={coverPreviewUrl}
            existingCoverUrl={
              mode === 'edit'
                ? resolveMediaUrl(activity?.coverImageUrl)
                : null
            }
            categoryCoverUrl={coverCategory.url}
            categoryColor={coverCategory.color}
            fileName={coverFile?.name ?? null}
            error={coverError ? t(coverError) : null}
            disabled={isSubmitting || coverBusy}
            onSelect={(file) => void selectCover(file)}
          />
        </FormSection>

        {coverFailure ? (
          <Notice tone="error">
            <p>
              {t(
                mode === 'create'
                  ? 'activityForm.cover.savedCreate'
                  : 'activityForm.cover.savedEdit',
              )}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                className="btn-secondary min-h-11 px-3 text-sm"
                disabled={coverBusy}
                aria-busy={coverBusy}
                onClick={() => void retryCoverUpload()}
              >
                {coverBusy
                  ? t('activityForm.cover.retrying')
                  : t('activityForm.cover.retry')}
              </button>
              <button
                type="button"
                className="btn-ghost min-h-11 px-2 text-sm"
                disabled={coverBusy}
                onClick={continueWithoutCover}
              >
                {t('activityForm.cover.continue')}
              </button>
            </div>
          </Notice>
        ) : null}

        {coverRetryError ? (
          <Notice tone="error">{t(coverRetryError)}</Notice>
        ) : null}

        {saveError ? <Notice tone="error">{t(saveError)}</Notice> : null}

        {draftNotice ? (
          <Notice
            tone={
              draftNotice === 'activityForm.draft.saved'
                ? 'success'
                : draftNotice === 'activityForm.draft.saveError'
                  ? 'error'
                  : 'info'
            }
          >
            <div className="flex items-start justify-between gap-3">
              <span>{t(draftNotice)}</span>
              <button
                type="button"
                className="shrink-0 text-xs font-bold underline"
                onClick={() => setDraftNotice(null)}
              >
                {t('common.close')}
              </button>
            </div>
          </Notice>
        ) : null}

        {mode === 'edit' && activity ? (
          <FormSection
            title={t('activityForm.sections.creatorActions')}
            icon={KuniriIcons.pencilSimple}
          >
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                className="btn-secondary min-h-11 border-danger px-4 text-sm text-danger hover:bg-[var(--danger-soft)]"
                disabled={isSubmitting || cancelBusy}
                onClick={() => {
                  setCancelError(null)
                  setCancelOpen(true)
                }}
              >
                <KuniriIcons.x
                  size={17}
                  weight={kuniriIconWeight}
                  aria-hidden="true"
                />
                {t(
                  recurringEdit
                    ? 'activityForm.cancelActivity.recurringAction'
                    : 'activityForm.cancelActivity.action',
                )}
              </button>
              <p className="max-w-prose text-xs leading-relaxed text-muted">
                {t(
                  recurringEdit
                    ? 'activityForm.cancelActivity.recurringHint'
                    : 'activityForm.cancelActivity.hint',
                )}
              </p>
            </div>
            {cancelError ? (
              <p className="mt-2 text-sm font-semibold text-danger">
                {t(cancelError)}
              </p>
            ) : null}
          </FormSection>
        ) : null}

        <div className="activity-form__actions">
          <button
            type="submit"
            className="btn-primary w-full"
            disabled={
              isSubmitting ||
              profileBlocked ||
              coverBusy ||
              coverFailure !== null
            }
            aria-busy={isSubmitting}
            aria-describedby={profileBlocked ? 'activity-profile-gate' : undefined}
          >
            {isSubmitting
              ? t(
                  mode === 'create'
                    ? 'activityForm.actions.publishing'
                    : 'activityForm.actions.saving',
                )
              : t(
                  mode === 'create'
                    ? 'activityForm.actions.publish'
                    : 'activityForm.actions.save',
                )}
          </button>
          {mode === 'create' ? (
            <button
              type="button"
              className="btn-ghost w-full text-sm"
              disabled={isSubmitting || coverBusy || createdId !== null}
              onClick={storeDraft}
            >
              {t('activityForm.actions.draft')}
            </button>
          ) : null}
          {draftAvailable ? (
            <button
              type="button"
              className="btn-ghost w-full text-sm"
              onClick={discardDraft}
            >
              {t('activityForm.actions.discardDraft')}
            </button>
          ) : null}
          {mode === 'edit' && activity ? (
            <Link
              to={`/activity/${activity.id}`}
              className="btn-ghost w-full text-center text-sm"
            >
              {t('common.cancel')}
            </Link>
          ) : null}
        </div>
      </form>

      {mode === 'edit' && activity ? (
        <ConfirmDialog
          open={cancelOpen}
          title={t(
            recurringEdit
              ? 'activityForm.cancelActivity.recurringTitle'
              : 'activityForm.cancelActivity.title',
          )}
          body={t(
            recurringEdit
              ? 'activityForm.cancelActivity.recurringBody'
              : 'activityForm.cancelActivity.body',
          )}
          confirmLabel={
            cancelBusy
              ? t('activityForm.cancelActivity.cancelling')
              : t(
                  recurringEdit
                    ? 'activityForm.cancelActivity.recurringConfirm'
                    : 'activityForm.cancelActivity.confirm',
                )
          }
          cancelLabel={t('activityForm.cancelActivity.back')}
          busy={cancelBusy}
          destructive
          onConfirm={() => void cancelActivity()}
          onCancel={() => setCancelOpen(false)}
        />
      ) : null}

      <ConfirmDialog
        open={blocker.state === 'blocked'}
        title={t('activityForm.unsaved.title')}
        body={t('activityForm.unsaved.body')}
        confirmLabel={t('activityForm.unsaved.confirm')}
        cancelLabel={t('activityForm.unsaved.cancel')}
        onConfirm={() => {
          if (blocker.state === 'blocked') blocker.proceed()
        }}
        onCancel={() => {
          if (blocker.state === 'blocked') blocker.reset()
        }}
      />

      {profileBlocked ? (
        <span className="sr-only" id="activity-profile-gate">
          {t('activityForm.profile.blocked')}
        </span>
      ) : null}
    </>
  )
}
